import { type } from "arktype";
/**
 * Per-user acknowledgments of the "planned minimum is below the bank-reported minimum" warning, stored in
 * `user_app.payback_min_acks_raw`. Part 4 of the debt-Payback below-bank-min work (T-214) — the persisted,
 * re-arming state behind the red attention breadcrumb (Budget tab → Payback Pouch row → Payback Plan card).
 *
 * Shape: `{ [accountId]: ackedBankMin }`. The VALUE is the bank-reported minimum (`Account.c_min_payment`,
 * a **float in the budget's currency units** — NOT cents) that the user acknowledged for that debt.
 *
 * Why the value and not a boolean: the ack is value-keyed so it **re-arms on a climb**. The breadcrumb shows
 * for a debt while `belowBankMin && (ack == null || currentBankMin - ack > PAYBACK_EPSILON)` — so if the bank
 * later raises its reported minimum above what the user acked against, the (now more dangerous) warning
 * re-surfaces. Fixing the plan (planned min ≥ bank min) clears it by derivation regardless of the ack.
 * The re-arm comparison itself lives with the derivation (mobile `paybackAttentionCore`), since it needs the
 * per-debt `belowBankMin` state; this module owns only the stored shape + its normalization.
 *
 * A NULL column reads as `{}` (no acks) via {@link normalizePaybackMinAcks} — no backfill needed.
 *
 * NOTE: plain-object index schema (no `.or()`), so it is safe under the mobile arktype Hermes stub — though
 * validation is server-only regardless. The normalizers below are plain JS (never invoke arktype at runtime)
 * so they behave identically on mobile, where arktype is a no-op stub.
 */
export const paybackMinAcksSchema = type({
    "[string]": "number",
});
/** Fresh, unshared default (no acknowledgments). Mirrors the `createEmptyProgress` convention. */
export function createEmptyPaybackMinAcks() {
    return {};
}
function isRecord(x) {
    return typeof x === "object" && x !== null && !Array.isArray(x);
}
/**
 * Interpret a raw `payback_min_acks_raw` value (possibly NULL / partial / malformed) into a complete, valid
 * {@link PaybackMinAcks}. Read-time defaulter (no backfill) AND server-side write normalizer — plain JS so it
 * behaves identically on mobile (where arktype is stubbed).
 *
 * Keeps only entries with a non-empty string key and a **finite, non-negative** numeric value (a bank
 * minimum is a currency amount, never negative or NaN); everything else is dropped, so a malformed client
 * payload can never land a bad shape in the column.
 */
export function normalizePaybackMinAcks(raw) {
    if (!isRecord(raw)) {
        return {};
    }
    const out = {};
    for (const [accountId, value] of Object.entries(raw)) {
        if (accountId.length > 0 && typeof value === "number" && Number.isFinite(value) && value >= 0) {
            out[accountId] = value;
        }
    }
    return out;
}
/**
 * Upsert a single per-debt acknowledgment: the user acked `accountId` against the current bank minimum
 * `bankMin`. Returns a NEW normalized map (never mutates `current`). A non-finite / negative `bankMin` is a
 * no-op (returns the normalized current) — the write is server-authoritative. Used by the ack endpoint.
 *
 * `validAccountIds` (when supplied) PRUNES the result to those ids — the map is rewritten wholesale on every
 * write, so the server passes the caller's own account ids to drop entries for accounts that were deleted or
 * never belonged to them. Without it the map would grow forever (it rides down in every `getUserData`
 * bootstrap and persists to AsyncStorage) and would accept arbitrary attacker-supplied keys.
 */
export function withPaybackMinAck(current, p) {
    const merged = normalizePaybackMinAcks(current);
    if (p.accountId.length > 0 && typeof p.bankMin === "number" && Number.isFinite(p.bankMin) && p.bankMin >= 0) {
        merged[p.accountId] = p.bankMin;
    }
    if (!p.validAccountIds) {
        return merged;
    }
    const keep = new Set(p.validAccountIds);
    const pruned = {};
    for (const [accountId, value] of Object.entries(merged)) {
        if (keep.has(accountId)) {
            pruned[accountId] = value;
        }
    }
    return pruned;
}
//# sourceMappingURL=paybackMinAcks.models.js.map